<script lang="ts">
  /**
   * The Together tab (Versus before `docs/decisions/0054`): playing with or
   * against somebody, on the shell's `TogetherTemplate`. The tab is named
   * "Together" in every one of the owner's games, because it holds Versus
   * *and* Co-op.
   *
   * - **Start a game**: three filled tiles, the same weight as Home's New
   *   game tile, because starting is what this half of the tab is for. Play
   *   the computer and Create a room side by side, Co-op under them (the
   *   template's odd last tile spans its row). Each opens its own start
   *   screen (`StartScreen.svelte`), preset to what was pressed.
   * - **On this device** (`docs/decisions/0059`): players take turns on one
   *   phone, tablet or computer. It opens the setup (`LocalSetupPicker`),
   *   needs no server, and so is never dimmed offline.
   * - **Join a game**: the outlined tile, the lighter door. It opens the join
   *   screen (`JoinScreen.svelte`); there are no code boxes on the tab.
   *
   * No name is asked here: a name is asked where a seat is taken, and only of
   * a guest. Rooms are not kept, so there is no list of games under the
   * doors (the template's `list` is not passed).
   *
   * Every door but "On this device" needs the server. Without a connection
   * those are dimmed and a sentence says why (the template's announced
   * `notice`); solo play, on Home, and "On this device" are unaffected
   * (BASELINE 5).
   */
  import { Icon, TogetherTemplate } from '@artibens/app-shell';
  import type { TogetherStart } from '@artibens/app-shell';

  import GameGlyph from '../glyphs/GameGlyph.svelte';
  import { connection } from '../nav/online.svelte.js';

  const {
    onStart,
    onJoin,
    onLocal,
  }: {
    /** Opens the start screen for one of the three doors. */
    onStart: (preset: 'ai' | 'duel' | 'coop') => void;
    /** Opens the join screen. */
    onJoin: () => void;
    /** Opens the setup of a round on one device. */
    onLocal: () => void;
  } = $props();

  const offline = $derived(!connection.online);

  const starts = $derived<TogetherStart[]>([
    {
      id: 'ai',
      title: 'Play the computer',
      detail: 'Four levels, every kind of duel',
      icon: computerIcon,
      disabled: offline,
      testid: 'play-computer',
      onclick: () => {
        onStart('ai');
      },
    },
    {
      id: 'duel',
      title: 'Create a room',
      detail: 'A duel with a friend',
      icon: roomIcon,
      tone: 'second',
      disabled: offline,
      testid: 'create-room-row',
      onclick: () => {
        onStart('duel');
      },
    },
    {
      id: 'coop',
      title: 'Co-op',
      detail: 'One well, two players',
      icon: coopIcon,
      disabled: offline,
      testid: 'coop-row',
      onclick: () => {
        onStart('coop');
      },
    },
    {
      id: 'local',
      title: 'On this device',
      detail: 'Take turns on one phone, tablet or computer',
      icon: deviceIcon,
      tone: 'second',
      testid: 'local-turns',
      onclick: onLocal,
    },
  ]);
</script>

{#snippet computerIcon()}<GameGlyph name="computer" size={24} />{/snippet}
{#snippet roomIcon()}<Icon name="swords" size={24} />{/snippet}
{#snippet coopIcon()}<Icon name="together" size={24} />{/snippet}
{#snippet deviceIcon()}<GameGlyph name="device" size={24} />{/snippet}

<TogetherTemplate
  startLabel="Start a game"
  {starts}
  join={{
    title: 'Join a game',
    detail: 'Got a code from a friend? Type it here.',
    disabled: offline,
    onclick: onJoin,
    testid: 'join-tile',
  }}
  notice={offline
    ? 'Playing online needs a connection. Solo and On this device work offline.'
    : undefined}
  testid="together"
  testids={{ notice: 'versus-offline' }}
/>
